import { showNotification } from '@mantine/notifications';
import type { NotificationRuleOut } from '@repo/api-codegen';
import {
	useCreateNotificationRule,
	useDeleteNotificationRule,
	useListNotificationRules,
} from '@repo/api-codegen';
import { useState } from 'react';
import { NotificationRuleFormController } from '../NotificationRuleForm';
import { NotificationRulesList } from './components/NotificationRulesList';

interface NotificationRulesControllerProps {
	onEditRule?: (ruleId: string) => void;
}

export function NotificationRulesController({
	onEditRule,
}: NotificationRulesControllerProps = {}) {
	const [editingRuleId, setEditingRuleId] = useState<string | null>(null);

	const { data: rulesData, isLoading } = useListNotificationRules({});
	const { mutateAsync: createRule } = useCreateNotificationRule();
	const { mutateAsync: deleteRule } = useDeleteNotificationRule();

	const rules = rulesData?.results || [];

	const handleCreateRule = async () => {
		try {
			const newRule = await createRule({
				body: {
					title: 'New notification rule',
					description: '',
					is_enabled: true,
				},
			});

			return newRule;
		} catch (error) {
			showNotification({
				title: 'Error',
				message: 'Failed to create notification rule',
				color: 'red',
				autoClose: 1500,
			});
		}
	};

	const handleDeleteRule = async (ruleId: string) => {
		try {
			await deleteRule({
				pathParams: { ruleId },
			});

			showNotification({
				title: 'Notification rule deleted',
				message: '',
				autoClose: 1500,
			});
		} catch (error) {
			showNotification({
				title: 'Error',
				message: 'Failed to delete notification rule',
				color: 'red',
				autoClose: 1500,
			});
		}
	};

	const handleEditRule = (rule: NotificationRuleOut) => {
		if (onEditRule) {
			onEditRule(rule.id);
		} else {
			setEditingRuleId(rule.id);
		}
	};

	const handleCloseForm = () => {
		setEditingRuleId(null);
	};

	// Show form when editing
	if (editingRuleId) {
		return (
			<NotificationRuleFormController
				ruleId={editingRuleId}
				onClose={handleCloseForm}
			/>
		);
	}

	// Show list view
	return (
		<NotificationRulesList
			rules={rules}
			isLoading={isLoading}
			onCreateRule={handleCreateRule}
			onEditRule={handleEditRule}
			onDeleteRule={handleDeleteRule}
		/>
	);
}
